import http from '@frontend/api/http';
import { HYPATIA_URL, OPAL_BACKEND_URL } from '@frontend/envConfig';
import {
  AgentExecutionResponse,
  AgentFileUrl,
  AgentVersionListResponse,
  EvaluationSample,
  EvaluationSampleCreate,
  EvaluationSamplesResponse,
  MemoryChunkList,
  SpecializedAgent,
  SpecializedAgentExecution,
  SpecializedAgentListItem,
} from '@frontend/types';

import { AgentIdStatus } from '../components/types/agent-id';

import { ensureUtf8 } from './utf8-converter';

export function getSpecializedAgents({
  instanceId,
  includeInactive = true,
  page = 1,
  pageSize = 1000,
  includeDeleted = false,
}: {
  instanceId: string;
  page?: number;
  pageSize?: number;
  includeDeleted?: boolean;
  includeInactive?: boolean;
}) {
  return async () => {
    const res = await http.get<{ items: SpecializedAgentListItem[] }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized`,
      {
        params: {
          page,
          page_size: pageSize,
          include_deleted: includeDeleted,
          include_inactive: includeInactive,
        },
      }
    );
    return res.data.items;
  };
}

getSpecializedAgents.key = (instanceId: string, page = 1, pageSize = 20, includeDeleted = false) =>
  ['opal:specialized-agents', instanceId, page, pageSize, includeDeleted] as const;

export function getSpecializedAgent(url: string) {
  return async () => {
    const res = await http.get<SpecializedAgent>(url);
    return res.data;
  };
}

export function createSpecializedAgent(agent: SpecializedAgent, instanceId: string) {
  return async () => {
    return http.post<SpecializedAgent>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized`,
      agent
    );
  };
}

export async function uploadFile(
  file: File,
  customerId: string,
  instanceId: string
): Promise<AgentFileUrl> {
  // Ensure text files are UTF-8 encoded before uploading
  const processedFile = await ensureUtf8(file);

  // Step 1: Request signed URL
  const signedUrlResponse = await http.post<{
    signed_url: string;
    file_id: string;
    expires_at: number;
    content_type: string;
  }>(
    `${OPAL_BACKEND_URL}/api/v2/customer/${customerId}/files/upload/signed-url?instance_id=${instanceId}`,
    {
      filename: processedFile.name,
      mime_type: processedFile.type,
      size: processedFile.size,
    }
  );

  const { signed_url, file_id } = signedUrlResponse.data;

  // Step 2: Upload directly to GCS
  await fetch(signed_url, {
    method: 'PUT',
    body: processedFile,
    headers: {
      'Content-Type': processedFile.type,
    },
  });

  // Step 3: Create file metadata
  const metadataResponse = await http.post<{
    file_id: string;
    name: string;
    mime_type: string;
    size: number;
    uri: string;
    customer_id: string;
    link: string;
  }>(
    `${OPAL_BACKEND_URL}/api/v2/customer/${customerId}/files/${file_id}/create?instance_id=${instanceId}`,
    {
      filename: processedFile.name,
      mime_type: processedFile.type,
      size: processedFile.size,
    }
  );

  const data = metadataResponse.data;
  return {
    url: data.uri,
    type: data.mime_type,
    description: data.name || null,
  } as AgentFileUrl;
}

export function updateSpecializedAgent(url: string, agent: SpecializedAgent) {
  return async () => {
    return http.patch(url, agent);
  };
}

export function deleteSpecializedAgent(deleteUrl: string) {
  return async () => {
    return http.delete(deleteUrl);
  };
}

export function checkAgentIdAvailability(agentId: string, instanceId: string) {
  return async () => {
    const res = await http.post<AgentIdStatus>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/check-agent-id`,
      {
        agent_id: agentId,
      }
    );
    return res.data;
  };
}

export function executeSpecializedAgent(executeUrl: string, parameters: Record<string, unknown>) {
  return async () => {
    const res = await http.post<AgentExecutionResponse>(
      executeUrl,
      { parameters },
      {
        params: {
          run_in_background: true,
        },
        timeout: 30 * 60 * 10000, // 10 minutes timeout for agent execution
      }
    );
    return res.data;
  };
}

export function getSpecializedAgentExecution(instanceId: string, executionId: string) {
  return async () => {
    const res = await http.get<SpecializedAgentExecution>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/executions/${executionId}`
    );
    return res.data;
  };
}

export function getAgentMemoryChunks(memoryUrl: string) {
  return async () => {
    const res = await http.get<MemoryChunkList>(`${memoryUrl}/chunks`);
    return res.data;
  };
}

export function exportSpecializedAgent(instanceId: string, agentId: string) {
  return async () => {
    const res = await http.get<{ data: unknown }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/export`
    );
    return res.data.data;
  };
}

export const importSpecializedAgent = (
  instanceId: string,
  agentData: object,
  mode: 'skip' | 'overwrite' = 'overwrite'
) => {
  return async () => {
    const res = await http.post(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/import?mode=${mode}`,
      agentData
    );
    return res.data;
  };
};

// Evaluation Samples API
export function getEvaluationSamples(instanceId: string, agentId: string) {
  return async () => {
    const res = await http.get<EvaluationSamplesResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples`
    );
    return res.data.data.samples;
  };
}

getEvaluationSamples.key = (instanceId: string, agentId: string) =>
  ['opal:evaluation-samples', instanceId, agentId] as const;

export function createEvaluationSample(
  instanceId: string,
  agentId: string,
  data: EvaluationSampleCreate
) {
  return async () => {
    const res = await http.post<{ data: EvaluationSample }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples`,
      data
    );
    return res.data.data;
  };
}

export function updateEvaluationSample(
  instanceId: string,
  agentId: string,
  sampleId: string,
  data: EvaluationSampleCreate
) {
  return async () => {
    const res = await http.patch<{ data: EvaluationSample }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples/${sampleId}`,
      data
    );
    return res.data.data;
  };
}

export function deleteEvaluationSample(instanceId: string, agentId: string, sampleId: string) {
  return async () => {
    await http.delete(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/evaluation-samples/${sampleId}`
    );
  };
}

export function stopSpecializedAgentExecution(
  instanceId: string,
  executionId: string,
  errorMessage: string
) {
  return async () => {
    const res = await http.patch<SpecializedAgentExecution>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/executions/${executionId}`,
      {
        error_message: errorMessage,
        status: 'FAILED',
      }
    );
    return res.data;
  };
}

// Agent Version History API
export function getSpecializedAgentVersions(instanceId: string, agentId: string, limit = 50) {
  return async () => {
    const res = await http.get<AgentVersionListResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/versions`,
      { params: { limit } }
    );
    return res.data;
  };
}

getSpecializedAgentVersions.key = (instanceId: string, agentId: string) =>
  ['opal:specialized-agent-versions', instanceId, agentId] as const;

export function getSpecializedAgentVersion(versionUrl: string) {
  return async () => {
    const res = await http.get<SpecializedAgent>(versionUrl);
    return res.data;
  };
}

getSpecializedAgentVersion.key = (versionUrl: string) =>
  ['opal:specialized-agent-version', versionUrl] as const;
